import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import clsx from 'clsx'
import { nanoid } from 'nanoid'

import { RoutingPaths } from 'app/routes/routes'

import { SearchExcursions } from 'shared/api/excursions'
import { IExcursionsList } from 'shared/api/excursions/list/model'
import { isShowingMplus } from 'shared/constants'
import ImageGlass from 'shared/images/glasss.png'
import SmallGhostImage from 'shared/images/small-ghost.png'
import { getDurationValue } from 'shared/lib/excursions/getValueInLists'
import { CardSmallImage } from 'shared/ui/card-small-image'
import { EmptyResults } from 'shared/ui/empty-result'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { ImageLazy } from 'shared/ui/image-lazy'
import { Loader } from 'shared/ui/loader'

import s from './styles.module.scss'

const EMPTY_PAGE = 0

const ExcursionsByDirection = () => {
  const navigate = useNavigate()

  const { t } = useTranslation()

  const { id } = useParams()

  const [useSearch] = useSearchParams()

  const nameDirection = useSearch.get('name')

  const [loading, setLoading] = useState<boolean>()
  const [listExcursions, setListExcursions] = useState<IExcursionsList[]>()

  const getListExcursions = async (id: string) => {
    setLoading(true)

    try {
      const { data: listExcursions } =
        await SearchExcursions.getSearchByDirection(id)

      setListExcursions(listExcursions?.data)
    } catch {
      setListExcursions([])
    } finally {
      setLoading(false)
    }
  }

  const onClick = (index: number) => {
    const currentCard = listExcursions?.[index]

    navigate(RoutingPaths.excursion.guideDetail.path(String(currentCard?.id)))
  }

  useEffect(() => {
    if (id) {
      getListExcursions(id)
    }
  }, [])

  const listExcursionsMemo = useMemo(
    () =>
      listExcursions?.map((item) => ({
        title: item?.name || '',
        detail: `${item?.company_guide?.name} • ${item?.excursion_type.value} • ${t(getDurationValue(item?.duration))}`,
        price: item.is_promo ? item.new_price : item.price,
        image: item?.preview_image_fullpath,
        old_price: item?.price,
        is_promo: item?.is_promo,
        is_bonus: item?.is_bonus,
        new_price: item.new_price,
      })),
    [listExcursions],
  )

  return (
    <div className={clsx('container', s.container)}>
      <div className={s.header}>
        <HeaderGoBack
          title={nameDirection || undefined}
          buttonWhite
        />
      </div>

      {loading && (
        <Loader
          type="excursions"
          marginTop={200}
        />
      )}

      <div className={s.list}>
        {listExcursionsMemo?.map((excursion, index) => (
          <CardSmallImage
            {...excursion}
            key={nanoid()}
            isShowingMplus={isShowingMplus}
            onClick={() => onClick(index)}
            errorImage={SmallGhostImage}
            isPromo={excursion.is_promo}
            isMbonus={excursion.is_bonus}
            oldPrice={excursion.old_price}
            newPrice={excursion.new_price}
          />
        ))}
      </div>

      {listExcursions?.length === EMPTY_PAGE && !loading ? (
        <div className={s.empty}>
          <EmptyResults
            image={
              <ImageLazy
                src={ImageGlass}
                alt="empty"
                className={s.image}
              />
            }
            title={t('excursions.empty_title')}
            subTitle={t('excursions.empty_description')}
          />
        </div>
      ) : null}
    </div>
  )
}

export default ExcursionsByDirection
